/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/11/22, 3:57 PM
 *
 */

import React from 'react';
import moment from 'moment';
import { Calendar, GooglePlayLogo, Thermometer } from 'phosphor-react';
import { isValidHttpUrl } from 'utils/isValidHttpUrl';

import { MemberTestList, MemberTestReport } from 'features/members/types';
import { utcDateToLocal } from 'features/members/utils/utcDateToLocal';

import { TableView } from 'components/Table';

export const URINE_ORDER = [
  'uro',
  'bil',
  'ket',
  'cre',
  'blo',
  'pro',
  'Alb',
  'nit',
  'leu',
  'glu',
  'sg',
  'ph',
  'vc',
  'ca',
];

export const URINE_TEST: { [key: string]: string } = {
  uro: 'Urobilinogen',
  bil: 'Bilirubin',
  ket: 'Ketone',
  cre: 'Creatinine',
  blo: 'Blood',
  pro: 'Protein',
  Alb: 'Micro Albumin',
  nit: 'Nitrite',
  leu: 'Leukocytes',
  glu: 'Glucose',
  sg: 'Specific Gravity',
  ph: 'ph',
  vc: 'Ascorbate',
  ca: 'Calcium',
};

type ProfileTestData = {
  id: number;
  app_slug: string;
  test_name: string;
  test_date: Date;
  temperature: null | string;
  tests: { [p: string]: any }[];
};
export const ProfileTestTableRow = ({ data }: { data?: ProfileTestData }) => {
  return data ? (
    <tr key={data.id}>
      <td className="whitespace-nowrap px-6 py-4 align-top text-xl font-medium capitalize text-gray-700">
        <div className="flex flex-col space-y-2">
          <span>{moment(utcDateToLocal(data.test_date)).format('MM/DD/YYYY')}</span>

          <span>{moment(utcDateToLocal(data.test_date)).format('h:mm A')}</span>
        </div>
      </td>
      <td className="whitespace-nowrap px-6 py-4 align-top text-xl font-medium capitalize text-gray-700">
        {data.temperature} {data.temperature && <>&deg; F</>}
      </td>
      <td className="space-y-2 whitespace-nowrap px-6 py-4 align-top text-xl capitalize">
        {data?.test_name?.toLowerCase() === 'urine'
          ? URINE_ORDER.map((element, index) => {
              const unit = data.tests.find((test) =>
                Object.keys(test)?.find(
                  (sub_test) => sub_test?.toLowerCase() === element?.toLowerCase()
                )
              )?.unit;

              return (
                <div key={index} className="flex space-x-2 text-gray-700">
                  <span className="font-medium text-gray-500">{URINE_TEST[element]} : </span>
                  <span className="font-medium line-clamp-1">
                    {
                      data.tests.find((test) =>
                        Object.keys(test)?.find(
                          (sub_test) => sub_test?.toLowerCase() === element?.toLowerCase()
                        )
                      )?.[element]
                    }
                  </span>
                  <span className="font-medium lowercase line-clamp-1">
                    {unit === 'n/a' ? '' : unit}
                  </span>
                </div>
              );
            })
          : data.tests.map((element, index) => {
              if (isValidHttpUrl(Object.values(element)[0])) {
                return (
                  <div key={index} className="flex space-x-2">
                    <a
                      href={Object.values(element)[0]}
                      className="cursor-pointer font-medium text-gray-600 underline hover:text-gray-800"
                    >
                      Report Link
                    </a>
                  </div>
                );
              }

              return (
                <div key={index} className="flex flex-col gap-4 text-gray-700">
                  <div className="flex gap-2">
                    <span className="font-medium text-gray-500">{Object.keys(element)[0]} : </span>
                    <span className="font-medium line-clamp-1">{Object.values(element)[0]}</span>
                    <span className="font-medium lowercase line-clamp-1">
                      {Object.values(element)?.[2]?.toLowerCase() === 'n/a'
                        ? ''
                        : Object.values(element)?.[2]?.toLowerCase()}
                    </span>
                  </div>

                  {Object.values(element)[1].length !== 0 && (
                    <div className="flex gap-2">
                      <span className="font-medium text-gray-500">Notes:</span>
                      <div className="flex flex-col">
                        {Object.values(element)[1].map((element: any, index: number) => (
                          <span key={index} className="line-clamp-2">
                            {element !== 'Bmi Percentile: ' && element}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
      </td>
    </tr>
  ) : (
    <tr />
  );
};
type ProfileTestGridViewProps = {
  testDetails: MemberTestList;
};
export const ProfileTestGridView: React.FC<ProfileTestGridViewProps> = ({ testDetails }) => {
  return (
    <div className="justify-stretch grid grid-cols-2 items-stretch gap-8 print:grid-cols-1 sm:grid-cols-1 sm:gap-6">
      {Object.keys(testDetails).length !== 0 &&
        testDetails.list.length !== 0 &&
        testDetails.list.map((test) => (
          <div className="flex items-stretch" key={test.id}>
            <div className="flex w-full flex-col gap-8 rounded-lg bg-gray-50 p-4 text-xl sm:w-full">
              <h1 className="text-2xl font-medium capitalize tracking-wider text-gray-900 print:hidden">
                {test.test_name} Report
              </h1>
              <div className="space-y-2">
                <div className="flex items-center space-x-4">
                  <p className="flex items-center space-x-2 text-xl font-medium tracking-wider text-gray-700">
                    <GooglePlayLogo size={18} />

                    <span>App:</span>
                  </p>
                  <h1 className="text-xl font-medium capitalize tracking-wider text-gray-500">
                    {test.app_slug}
                  </h1>
                </div>

                <div className="flex items-center space-x-4">
                  <p className="flex items-center space-x-2 text-xl font-medium tracking-wider text-gray-700">
                    <Calendar size={18} />

                    <span>Test Date:</span>
                  </p>
                  <h1 className="text-xl font-medium tracking-wider text-gray-500">
                    {utcDateToLocal(test.test_date)}
                  </h1>
                </div>
                <div className="flex items-center space-x-4">
                  <p className="flex items-center space-x-2 text-xl font-medium tracking-wider text-gray-700">
                    <Thermometer size={18} weight="duotone" />

                    <span>Temperature:</span>
                  </p>
                  <h1 className="text-xl font-medium tracking-wider text-gray-500">
                    {test?.temperature}
                  </h1>
                </div>

                <div className="flex flex-col  space-y-2 ">
                  <TableView
                    loading={false}
                    data={test.report}
                    search={false}
                    tableRowComponent={<ProfileTestGridTableRow />}
                    tableHeadings={['Test Name', 'Test Value', 'Test Note']}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};
type ProfileTestGridTableRowProps = {
  data?: MemberTestReport;
};
export const ProfileTestGridTableRow: React.FC<ProfileTestGridTableRowProps> = ({ data }) => {
  return data ? (
    <tr>
      <td className="whitespace-nowrap px-6 py-4 text-xl font-medium capitalize text-gray-700">
        <div className="flex flex-col">
          <span>{URINE_TEST[data.name] || data.name} </span>
        </div>
      </td>
      <td className="whitespace-nowrap px-6 py-4 text-xl font-medium capitalize text-gray-700">
        {isValidHttpUrl(data.value) ? (
          <a
            href={data.value}
            className="cursor-pointer font-medium text-gray-600 underline hover:text-gray-800"
          >
            Report Link
          </a>
        ) : (
          data.value
        )}
      </td>

      <td className="truncate px-6 py-4 text-xl font-medium capitalize text-gray-700">
        {data.note.length === 0
          ? 'N/A'
          : data.note.map((note, index) => (
              <span className="flex flex-col" key={index}>
                {note !== 'Bmi Percentile: ' && note}
              </span>
            ))}
      </td>
    </tr>
  ) : (
    <tr />
  );
};
